import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useAuth } from "@/hooks/useAuth";
import { useCategorias } from "../CategoriasContext";
import { useContaConfig } from "../ContaConfigContext";
import { useImpressaoConfig } from "../ImpressaoConfigContext";
import { useLeitores } from "../LeitoresContext";
import { useLocalizacoes } from "../LocalizacoesContext";
import { useOrdensDeImpressao } from "../OrdensDeImpressaoContext";
import { useSeriais } from "../SeriaisContext";

export type SincronizacaoContextType = {};

export const SincronizacaoContext = createContext<
  SincronizacaoContextType | undefined
>(undefined);

export function SincronizacaoProvider({ children }: { children: ReactNode }) {
  const { setIsLoading, setLoadingStep, setLoadingProgress } =
    useGlobalLoader();
  const { coletaCategorias } = useCategorias();
  const { getContaConfig } = useContaConfig();
  const { listarTodosOsHardwaresIds, impressaoConfig, getImpressaoConfig } =
    useImpressaoConfig();
  const { coletaLeitores } = useLeitores();
  const { coletaLocalizacoes } = useLocalizacoes();
  const { coletarOrdens } = useOrdensDeImpressao();
  const { coletaSeriais } = useSeriais();
  const { usuario } = useAuth();

  const sincronizarTudo = async () => {
    setIsLoading(true);

    const steps = [
      { label: "Carregando categorias...", fn: coletaCategorias },
      {
        label: "Carregando configurações de impressão...",
        fn: getImpressaoConfig,
      },
      { label: "Carregando configurações da conta...", fn: getContaConfig },
      { label: "Carregando hardwares...", fn: listarTodosOsHardwaresIds },
      { label: "Carregando leitores...", fn: coletaLeitores },
      { label: "Carregando localizações...", fn: coletaLocalizacoes },
      { label: "Carregando seriais...", fn: coletaSeriais },
    ];

    try {
      for (let i = 0; i < steps.length; i++) {
        setLoadingStep(steps[i].label);
        setLoadingProgress(Math.round((i / steps.length) * 100));
        await steps[i].fn();
      }
      setLoadingStep("Finalizado!");
      setLoadingProgress(100);
    } catch (error) {
      console.error("Erro na sincronização:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const sincronizarOrdens = async () => {
    try {
      await coletarOrdens();
    } catch (error) {
      console.error("Erro ao sincronizar ordens:", error);
    }
  };

  useEffect(() => {
    if (usuario && impressaoConfig) {
      sincronizarOrdens();
    }
  }, [usuario, impressaoConfig]);

  useEffect(() => {
    if (usuario) {
      sincronizarTudo();
    }
  }, [usuario]);

  return (
    <SincronizacaoContext.Provider value={{}}>
      {children}
    </SincronizacaoContext.Provider>
  );
}

export function useSincronizacao() {
  const context = useContext(SincronizacaoContext);
  if (!context) {
    throw new Error(
      "useSincronizacao deve ser usado dentro de um SincronizacaoProvider",
    );
  }
  return context;
}
